/**
 * Store katalógu splitrov — načítanie a správa typov splitrov.
 */
import { create } from 'zustand';
import { getErrorMessage } from '../utils/errorMessages';
import {
  SplitterCatalogItem,
  setSplitterCatalog,
  getSplitterCatalog,
} from '../utils/splitterCatalog';

const API_URL = process.env.REACT_APP_API_URL || '';

function authHeaders(): Record<string, string> {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

interface SplitterCatalogState {
  types: SplitterCatalogItem[];
  loading: boolean;
  error: string | null;
  loaded: boolean;

  fetchTypes: (includeInactive?: boolean) => Promise<void>;
  createType: (data: Partial<SplitterCatalogItem>) => Promise<void>;
  updateType: (id: number, data: Partial<SplitterCatalogItem>) => Promise<void>;
  deleteType: (id: number) => Promise<void>;
}

export const useSplitterCatalogStore = create<SplitterCatalogState>((set, get) => ({
  types: getSplitterCatalog(),
  loading: false,
  error: null,
  loaded: false,

  fetchTypes: async (includeInactive = false) => {
    set({ loading: true, error: null });
    try {
      const res = await fetch(
        `${API_URL}/api/splitter-types/?include_inactive=${includeInactive}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error('načítanie zlyhalo');
      const data: SplitterCatalogItem[] = await res.json();
      setSplitterCatalog(data.filter((t) => t.is_active !== false));
      set({ types: data, loaded: true, loading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať typy splitrov.'), loading: false });
    }
  },

  createType: async (data) => {
    const res = await fetch(`${API_URL}/api/splitter-types/`, {
      method: 'POST',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Nepodarilo sa vytvoriť typ splitra.');
    }
    await get().fetchTypes(true);
  },

  updateType: async (id, data) => {
    const res = await fetch(`${API_URL}/api/splitter-types/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Nepodarilo sa upraviť typ splitra.');
    }
    await get().fetchTypes(true);
  },

  deleteType: async (id) => {
    const res = await fetch(`${API_URL}/api/splitter-types/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.detail || 'Nepodarilo sa zmazať typ splitra.');
    }
    await get().fetchTypes(true);
  },
}));
